import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom'
import { debounce } from 'lodash'
import moment from 'moment'
import { capabilities } from '@homevest/utils'
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Input,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react'

import { StoreState } from 'store'
import * as SC from 'components/TailwindUIToolkit/StyledComponents'
import axios from 'lib/axios'
import { Comparators as Comp } from 'lib/hasura-comparators'
import { hasCapability } from 'lib/admin-perms'
import { getCloseLeadUrl } from 'lib/close'
import { getContactInfo } from 'lib/users'
import { normalizePersonSearchText } from 'lib/person-search'
import {
  UpupUserListSubscription,
  Users_Bool_Exp,
  useUpupUserListSubscription
} from 'graphql/generated'

const { USER_MANAGER } = capabilities.CAPABILITY_TYPES

type ContactDetails = {
  id?: string
  contact_type: string
  contact_info: string
}

type RelatedUser = {
  id: string
  close_lead_id?: string | null
  first_name?: string | null
  last_name?: string | null
  name?: string | null
}

type ContactableUser = {
  user_contact_details?: ContactDetails[]
  email?: string | null
  email2?: string | null
  phone?: string | null
  phone2?: string | null
}

type MergeStatusUser = RelatedUser & {
  user_contact_details?: ContactDetails[]
  email?: string | null
  email2?: string | null
  phone?: string | null
  phone2?: string | null
  primary_user_id?: string | null
  primary_user?: RelatedUser | null
  merged_at?: string | null
  merged_by_admin?: RelatedUser | null
}

type SearchResultUser = UpupUserListSubscription['users'][number]

const getResponseName = (name?: string | null): string => {
  const trimmedName = name?.trim()

  if (
    !trimmedName ||
    ['No Name Provided', 'null null', 'undefined undefined'].includes(trimmedName)
  ) {
    return ''
  }

  return trimmedName
}

const fullName = (user?: Pick<RelatedUser, 'first_name' | 'last_name' | 'name'> | null) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
  getResponseName(user?.name) ||
  'No Name Provided'

const getUserEmail = (user: ContactableUser): string =>
  getContactInfo('email', user.user_contact_details) || user.email || user.email2 || ''

const getUserPhone = (user: ContactableUser): string =>
  getContactInfo('phone', user.user_contact_details) || user.phone || user.phone2 || ''

const getDefaultSearchText = (user: MergeStatusUser): string => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ')

  return name || getResponseName(user.name) || getUserEmail(user) || getUserPhone(user) || ''
}

const normalizeMergeError = (err: unknown): string =>
  typeof err === 'string'
    ? err
    : (err as any)?.response?.data?.message ??
      (err as any)?.response?.data?.error ??
      (err as any)?.message ??
      (err as any)?.error ??
      'Merge failed'

const UserSummary: React.FC<
  React.PropsWithChildren<{ label: string; user: MergeStatusUser | SearchResultUser }>
> = ({ label, user }) => {
  const closeLeadUrl = getCloseLeadUrl(user.close_lead_id)

  return (
    <Box minW={0} flex={1} borderWidth='1px' borderColor='gray.200' borderRadius='md' p={4}>
      <Text mb={3} fontSize='sm' fontWeight='semibold' textTransform='uppercase' color='gray.500'>
        {label}
      </Text>
      <Stack as='dl' spacing={2} fontSize='sm' color='gray.700'>
        <Box>
          <Text as='dt' fontWeight='semibold' color='gray.900'>
            Name
          </Text>
          <Text as='dd'>{fullName(user)}</Text>
        </Box>
        <Box>
          <Text as='dt' fontWeight='semibold' color='gray.900'>
            Email
          </Text>
          <Text as='dd' overflowWrap='anywhere'>
            {getUserEmail(user) || '-'}
          </Text>
        </Box>
        <Box>
          <Text as='dt' fontWeight='semibold' color='gray.900'>
            Phone
          </Text>
          <Text as='dd'>{getUserPhone(user) || '-'}</Text>
        </Box>
        <Box>
          <Text as='dt' fontWeight='semibold' color='gray.900'>
            User ID
          </Text>
          <Text as='dd' overflowWrap='anywhere' fontFamily='mono' fontSize='xs'>
            {user.id}
          </Text>
        </Box>
        <Box>
          <Text as='dt' fontWeight='semibold' color='gray.900'>
            Close profile
          </Text>
          <Text as='dd'>
            {closeLeadUrl ? (
              <ChakraLink color='blue.600' href={closeLeadUrl} isExternal>
                View in Close
              </ChakraLink>
            ) : (
              '-'
            )}
          </Text>
        </Box>
      </Stack>
    </Box>
  )
}

const MergeUser: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { userId } = useParams<{ userId: string }>()
  const history = useHistory()
  const toast = useToast()
  const admin = useSelector((store: StoreState) => store.admin)
  const canMerge = hasCapability(admin, USER_MANAGER)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sourceUser, setSourceUser] = useState<MergeStatusUser | null>(null)
  const [isSourceLoading, setIsSourceLoading] = useState(false)
  const [statusError, setStatusError] = useState('')
  const [inputText, setInputText] = useState('')
  const [searchText, setSearchText] = useState('')
  const [destinationUser, setDestinationUser] = useState<SearchResultUser | null>(null)
  const [mergeError, setMergeError] = useState('')
  const [isMerging, setIsMerging] = useState(false)

  const normalizedSearchText = normalizePersonSearchText(searchText)

  const debouncedSetSearchText = useMemo(
    () => debounce((value: string) => setSearchText(value), 250, { trailing: true }),
    []
  )

  const searchWhere = useMemo<Users_Bool_Exp>(
    () => ({
      id: Comp.isNotEqual(userId),
      primary_user_id: Comp.isNull(),
      deleted_at: Comp.isNull(),
      user_searchable: {
        info: Comp.isLike(`%${normalizedSearchText}%`)
      }
    }),
    [normalizedSearchText, userId]
  )

  const [{ data: searchData, fetching: isSearching }] = useUpupUserListSubscription({
    variables: { where: searchWhere },
    pause:
      !isModalOpen ||
      isSourceLoading ||
      !!sourceUser?.primary_user_id ||
      !!destinationUser ||
      !normalizedSearchText
  })

  useEffect(() => {
    return () => debouncedSetSearchText.cancel()
  }, [debouncedSetSearchText])

  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    let isCurrentRequest = true
    const requestedUserId = userId

    setIsSourceLoading(true)
    setStatusError('')
    setMergeError('')
    setDestinationUser(null)
    setSourceUser(null)
    setInputText('')
    setSearchText('')

    axios
      .get<MergeStatusUser>(`/admin/users/${userId}`)
      .then(({ data }) => {
        if (!isCurrentRequest || data.id !== requestedUserId) {
          return
        }

        const defaultSearchText = getDefaultSearchText(data)

        setSourceUser(data)
        setInputText(defaultSearchText)
        setSearchText(defaultSearchText)
      })
      .catch((err: unknown) => {
        if (isCurrentRequest) {
          setStatusError(normalizeMergeError(err))
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsSourceLoading(false)
        }
      })

    return () => {
      isCurrentRequest = false
    }
  }, [isModalOpen, userId])

  if (!canMerge) {
    return null
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setSourceUser(null)
    setStatusError('')
    setInputText('')
    setSearchText('')
    setDestinationUser(null)
    setMergeError('')
    debouncedSetSearchText.cancel()
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value)
    debouncedSetSearchText(event.target.value)
  }

  const handleMerge = async () => {
    if (!destinationUser) {
      return
    }

    try {
      setIsMerging(true)
      setMergeError('')
      await axios.post(`/admin/users/${userId}/merge`, { primary_user_id: destinationUser.id })
      toast({
        title: `Merged into ${fullName(destinationUser)}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
      handleClose()
      history.push(`/users/${destinationUser.id}`)
    } catch (err: unknown) {
      const message = normalizeMergeError(err)
      setMergeError(message)
      toast({
        title: 'Error merging user',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setIsMerging(false)
    }
  }

  const users = normalizedSearchText ? searchData?.users ?? [] : []
  const mergedByName = sourceUser?.merged_by_admin ? fullName(sourceUser.merged_by_admin) : 'System'
  const hasFooter = Boolean(destinationUser && sourceUser && !sourceUser.primary_user_id)

  return (
    <>
      <SC.DropDownMenuItem onClick={() => setIsModalOpen(true)}>Merge</SC.DropDownMenuItem>
      <Modal
        isCentered
        isOpen={isModalOpen}
        motionPreset='none'
        onClose={handleClose}
        scrollBehavior='inside'
        size='3xl'
      >
        <ModalOverlay bg='blackAlpha.500' />
        <ModalContent maxH='calc(100vh - 4rem)' mx={4}>
          <ModalHeader px={6} pt={5} pb={4}>
            Merge User
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody px={6} pt={0} pb={hasFooter ? 4 : 6}>
            {isSourceLoading ? (
              <Flex justify='center' py={8}>
                <Spinner />
              </Flex>
            ) : statusError ? (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                {statusError}
              </Alert>
            ) : sourceUser?.primary_user_id ? (
              <Text color='gray.700'>
                This user was merged into{' '}
                <ChakraLink
                  as={RouterLink}
                  color='blue.600'
                  to={`/users/${sourceUser.primary_user_id}`}
                >
                  {fullName(sourceUser.primary_user)}
                </ChakraLink>{' '}
                {sourceUser.merged_at
                  ? `on ${moment(sourceUser.merged_at).format('MM/DD/yyyy @ hh:mm A')}`
                  : null}{' '}
                by {mergedByName}.
              </Text>
            ) : destinationUser && sourceUser ? (
              <Stack spacing={4}>
                {mergeError ? (
                  <Alert status='error' borderRadius='md'>
                    <AlertIcon />
                    {mergeError}
                  </Alert>
                ) : null}
                <Flex align='stretch' direction={{ base: 'column', md: 'row' }} gap={3}>
                  <UserSummary label='Source' user={sourceUser} />
                  <Flex align='center' color='gray.500' flexShrink={0} justify='center'>
                    <Icon
                      aria-label='Merges into'
                      as={ArrowRightIcon}
                      boxSize={5}
                      transform={{ base: 'rotate(90deg)', md: 'none' }}
                    />
                  </Flex>
                  <UserSummary label='Destination' user={destinationUser} />
                </Flex>
                <Alert status='error' borderRadius='md'>
                  <AlertIcon />
                  Merging {fullName(sourceUser)} into {fullName(destinationUser)} is irreversible.
                  It will also merge the two Close user profiles. Review the source and destination
                  before confirming.
                </Alert>
              </Stack>
            ) : (
              <Stack spacing={4}>
                <Box>
                  <Input
                    placeholder='Search users'
                    value={inputText}
                    onChange={handleSearchChange}
                  />
                  <Text mt={2} fontSize='sm' color='gray.600'>
                    Default search is for other users with the same name. You can edit to search for
                    a different name, or search by email or phone.
                  </Text>
                </Box>
                <Box
                  maxH='20rem'
                  overflowY='auto'
                  borderWidth='1px'
                  borderColor='gray.200'
                  borderRadius='md'
                >
                  {isSearching ? (
                    <Flex justify='center' py={6}>
                      <Spinner />
                    </Flex>
                  ) : users.length ? (
                    <Stack divider={<Divider />} spacing={0}>
                      {users.map((user) => (
                        <Flex key={user.id} align='center' justify='space-between' gap={4} p={3}>
                          <Box minW={0} fontSize='sm'>
                            <Text fontWeight='semibold' color='gray.900'>
                              {fullName(user)}
                            </Text>
                            <Text color='gray.600' overflowWrap='anywhere'>
                              {getUserEmail(user) || '-'}
                            </Text>
                            <Text color='gray.600'>{getUserPhone(user) || '-'}</Text>
                          </Box>
                          <Button
                            flexShrink={0}
                            colorScheme='blue'
                            size='sm'
                            onClick={() => {
                              setDestinationUser(user)
                              setMergeError('')
                            }}
                          >
                            Select
                          </Button>
                        </Flex>
                      ))}
                    </Stack>
                  ) : (
                    <Text p={4} textAlign='center' fontSize='sm' color='gray.500'>
                      No users found.
                    </Text>
                  )}
                </Box>
              </Stack>
            )}
          </ModalBody>

          {hasFooter ? (
            <ModalFooter justifyContent='space-between' px={6} pt={0} pb={6}>
              <Button
                variant='outline'
                onClick={() => setDestinationUser(null)}
                isDisabled={isMerging}
              >
                Back
              </Button>
              <Button colorScheme='red' onClick={handleMerge} isLoading={isMerging}>
                Confirm merge
              </Button>
            </ModalFooter>
          ) : null}
        </ModalContent>
      </Modal>
    </>
  )
}

export default MergeUser
